import React, { ComponentType, JSX, MouseEvent, useEffect, useState } from 'react';

import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';
import { Link } from '~/lib/react-router-dom';

import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import { Box, Button, Container, Menu, MenuItem, Stack } from '@mui/material';

import { CruiseOffersProvider } from '~/components/Cruises/context/useCruiseOffers';
import RateCodeCreationPage from '~/components/Cruises/pages/RateCodeCreation/RateCodeCreationPage';
import RateGroupingPage from '~/components/Cruises/pages/RateGrouping/RateGroupingPage';

import CruiseLines from '../CruiseLines/CruiseLinesPage';
import DealCrawlerPage from '../DealCrawlerPage/DealCrawlerPage';
import DealPageManagement from '../DealsPage/DealPageManagement';
import InclusionsPage from '../Inclusions/InclusionsListPage';
import PortsPage from '../Ports/PortsPage';
import RateCodeManagementPage from '../RateCodeManagement/RateCodeManagementPage';
import ShipsPage from '../Ships/ShipsPage';

import BookingsPage from './components/Bookings/BookingsPage';
import CabinCategoriesPage from './components/CabinCategories/CabinCategoriesPage';
import CabinCategoriesGroupPage from './components/CabinCategoriesGroup/CabinCategoriesGroupPage';
import CruiseInvalidOffers from './components/CruiseOffers/CruiseInvalidOffers';
import CruiseOfferList from './components/CruiseOffers/CruiseOfferList';
import CruiseSearch from './components/CruiseSearch/CruiseSearch';
import SailingPage from './components/Sailings/SailingPage';

type MenuItemType = {
  name: string;
  href: string;
  component: ComponentType<any> | null;
  includeSearch?: boolean;
  isDisabled?: boolean;
};

const CruiseOfferMenu: ReadonlyArray<MenuItemType> = [
  { name: 'Flash Offers', href: '/tours/lists/le', component: null },
  { name: 'Cruisebank Offers', href: '/cruises/offers', component: CruiseOfferList, includeSearch: true },
  { name: 'Cruisebank Sailings', href: '/cruises/sailings', component: SailingPage },
  { name: 'Check Invalid Offers', href: '/cruises/invalid-offers', component: CruiseInvalidOffers },
] as const;

const CruiseCMSMenu: ReadonlyArray<MenuItemType> = [
  { name: 'Cruise Lines', href: '/cruises/cruise-lines', component: CruiseLines },
  { name: 'Ships', href: '/cruises/ships', component: ShipsPage },
  { name: 'Cabin Categories', href: '/cruises/cabin-categories', component: CabinCategoriesPage },
  { name: 'Cabin Categories Groups', href: '/cruises/cabin-categories-groups', component: CabinCategoriesGroupPage },
  { name: 'Ports', href: '/cruises/ports', component: PortsPage },
] as const;

const RatesPromosMenu: ReadonlyArray<MenuItemType> = [
  { name: 'Rate Code Management', href: '/cruises/rate-code-management', component: RateCodeManagementPage },
  { name: 'Rate Code Creation', href: '/cruises/rate-code-creation', component: RateCodeCreationPage },
  { name: 'Rate Grouping', href: '/cruises/rate-grouping', component: RateGroupingPage },
  // TODO: Remove menu and old promotions pagehttps://aussiecommerce.atlassian.net/browse/CRUZ-2604
  // { name: 'Promotion Management', href: '/cruises/promotion-management', component: PromotionManagementPage },
  { name: 'Inclusions', href: '/cruises/inclusions', component: InclusionsPage },
  { name: 'Deals Pages', href: '/cruises/deals', component: DealPageManagement },
  { name: 'Deal Page Crawler', href: '/cruises/deal-crawler', component: DealCrawlerPage },
] as const;

const BookingsMenu: ReadonlyArray<MenuItemType> = [
  { name: 'Logs', href: '/cruises/booking-logs', component: BookingsPage },
] as const;

const NavigationMenu = [
  { name: 'Offers', menu: CruiseOfferMenu },
  { name: 'Cruise CMS', menu: CruiseCMSMenu },
  { name: 'Rates & Promos', menu: RatesPromosMenu },
  { name: 'Bookings', menu: BookingsMenu },
];

const setDefaultItem = (locationPathname: string) => {
  const defaultItem = CruiseOfferMenu[1];

  const locationItem = NavigationMenu.flatMap((item) => item.menu).find((item) => item.href === locationPathname);
  return locationItem !== defaultItem ? locationItem : defaultItem;
};

function CruisesPage(): JSX.Element {
  const location = useLocation();
  const defaultItem = setDefaultItem(location.pathname);
  const [currentItem, setCurrentItem] = useState(defaultItem);
  const [currentMenu, setCurrentMenu] = useState(null);
  const title = `CRUISE - ${currentItem.name}`;
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const handleNavigation = (event: MouseEvent<HTMLElement>, menu: ReadonlyArray<MenuItemType>) => {
    setAnchorEl(event.currentTarget);
    setCurrentMenu(menu);
    setOpen(true);
  };
  const handleMenuClose = () => {
    setOpen(false);
    setAnchorEl(null);
  };
  const handleMenuClick = (item) => {
    setCurrentItem(item);
    handleMenuClose();
  };
  useEffect(() => {
    setCurrentItem(defaultItem);
  }, [defaultItem]);

  return (
    <CruiseOffersProvider>
      <Container maxWidth="xl">
        <Stack direction="row" spacing={1}>
          {NavigationMenu.map((item) => (
            <Button
              key={item.name}
              onClick={(event) => handleNavigation(event, item.menu)}
              endIcon={<KeyboardArrowDown />}
            >
              {item.name}
            </Button>
          ))}

          <Menu open={open} anchorEl={anchorEl} onClose={handleMenuClose}>
            {currentMenu &&
              currentMenu
                .filter((item) => !item.isDisabled)
                .map((item) => (
                  <MenuItem key={item.name} onClick={() => handleMenuClick(item)} to={item.href} component={Link}>
                    {item.name}
                  </MenuItem>
                ))}
          </Menu>
        </Stack>

        <Helmet>
          <title>{title}</title>
        </Helmet>

        {currentItem.includeSearch && <CruiseSearch />}

        <Box mt={3}>
          <Box component={currentItem?.component} />
        </Box>
      </Container>
    </CruiseOffersProvider>
  );
}

export default CruisesPage;
